import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid2 as Grid, Chip, Button,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, FormControlLabel, Switch, Tooltip,
  Divider, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WorkspacePremium as PlanIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  ConfirmationNumber as TicketIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Plan {
  id: number;
  codigo: string;
  nome: string;
  descricao: string | null;
  preco_label: string | null;
  preco_mensal: number | null;
  eventos_mes: number | null;
  bilhetes_max: number | null;
  convidados_max: number | null;
  membros_equipa: number | null;
  comissao_pct: number | null;
  // features
  pass_ativo: boolean;
  cashless_ativo: boolean;
  nfc_ativo: boolean;
  chat_ativo: boolean;
  waitlist_ativa: boolean;
  gamificacao_ativa: boolean;
  beach_club_ativo: boolean;
  resort_ativo: boolean;
  documentos_ativo: boolean;
  sms_ativo: boolean;
  formulario_inscricao: boolean;
  analytics_avancado: boolean;
  api_access: boolean;
  white_label: boolean;
  suporte_dedicado: boolean;
  // meta
  destaque: boolean;
  ordem: number;
  ativo: boolean;
}

const EMPTY_PLAN: Omit<Plan, 'id'> = {
  codigo: '', nome: '', descricao: '', preco_label: '', preco_mensal: null,
  eventos_mes: null, bilhetes_max: null, convidados_max: null, membros_equipa: null, comissao_pct: null,
  pass_ativo: false, cashless_ativo: false, nfc_ativo: false, chat_ativo: false,
  waitlist_ativa: false, gamificacao_ativa: false, beach_club_ativo: false,
  resort_ativo: false, documentos_ativo: false, sms_ativo: false, formulario_inscricao: false, analytics_avancado: false,
  api_access: false, white_label: false, suporte_dedicado: false,
  destaque: false, ordem: 0, ativo: true,
};

const FEATURES: { key: keyof Omit<Plan, 'id'>; label: string; group: string }[] = [
  { key: 'pass_ativo',         label: 'Pass / Bilhetes pagos',       group: 'Bilheteira' },
  { key: 'cashless_ativo',     label: 'Cashless (POS / Barracas)',   group: 'Bilheteira' },
  { key: 'nfc_ativo',          label: 'NFC / Pulseiras',             group: 'Bilheteira' },
  { key: 'chat_ativo',         label: 'Chat do Evento',              group: 'Comunicação' },
  { key: 'waitlist_ativa',     label: 'Lista de Espera',             group: 'Comunicação' },
  { key: 'gamificacao_ativa',  label: 'Gamificação',                 group: 'Engagement' },
  { key: 'documentos_ativo',   label: 'Documentos',                  group: 'Conteúdo' },
  { key: 'formulario_inscricao', label: 'Formulário de Inscrição (workshop/conferência)', group: 'Conteúdo' },
  { key: 'sms_ativo',          label: 'SMS em massa',                group: 'Comunicação' },
  { key: 'beach_club_ativo',   label: 'Beach Club / Zonas',          group: 'Conteúdo' },
  { key: 'resort_ativo',       label: 'Resort / Multi-Day',          group: 'Conteúdo' },
  { key: 'analytics_avancado', label: 'Analytics Avançado',          group: 'Plataforma' },
  { key: 'api_access',         label: 'Acesso à API',                group: 'Plataforma' },
  { key: 'white_label',        label: 'White-label',                 group: 'Plataforma' },
  { key: 'suporte_dedicado',   label: 'Suporte Dedicado',            group: 'Plataforma' },
];

const FEATURE_GROUPS = ['Bilheteira', 'Comunicação', 'Engagement', 'Conteúdo', 'Plataforma'];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Plans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [form, setForm] = useState<Omit<Plan, 'id'>>(EMPTY_PLAN);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/plans');
      setPlans((res.data?.data || []).map((p: any) => ({
        ...p,
        pass_ativo: !!p.pass_ativo, cashless_ativo: !!p.cashless_ativo, nfc_ativo: !!p.nfc_ativo,
        chat_ativo: !!p.chat_ativo, waitlist_ativa: !!p.waitlist_ativa, gamificacao_ativa: !!p.gamificacao_ativa,
        beach_club_ativo: !!p.beach_club_ativo, resort_ativo: !!p.resort_ativo, documentos_ativo: !!p.documentos_ativo, sms_ativo: !!p.sms_ativo,
        formulario_inscricao: !!p.formulario_inscricao,
        analytics_avancado: !!p.analytics_avancado, api_access: !!p.api_access,
        white_label: !!p.white_label, suporte_dedicado: !!p.suporte_dedicado,
        destaque: !!p.destaque, ativo: !!p.ativo,
      })));
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditingPlan(null);
    setForm({ ...EMPTY_PLAN });
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(plan: Plan) {
    setEditingPlan(plan);
    setForm({ ...plan });
    setFormError(null);
    setDialogOpen(true);
  }

  function setF(key: keyof typeof form, value: any) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.codigo.trim()) { setFormError('O código é obrigatório'); return; }
    if (!form.nome.trim()) { setFormError('O nome é obrigatório'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, form);
        setSuccess('Plano actualizado com sucesso');
      } else {
        await api.post('/admin/plans', form);
        setSuccess('Plano criado com sucesso');
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setFormError(e.response?.data?.error || e.message || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deletingPlan) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/plans/${deletingPlan.id}`);
      setSuccess('Plano eliminado');
      setDeleteDialogOpen(false);
      setDeletingPlan(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao eliminar');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  }

  const numOrNull = (v: string) => v === '' ? null : Number(v);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PlanIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Planos de Eventos</Typography>
            <Typography variant="body2" color="text.secondary">Configure os planos e funcionalidades disponíveis</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            disabled={syncing}
            title="Sobe os eventos de cada entidade até ao plano da entidade. Nunca baixa o plano de um evento."
            onClick={async () => {
              setSyncing(true);
              setError(null);
              try {
                const res = await api.post('/admin/events/sync-plans', {});
                const { updated = 0, skipped = 0 } = res.data;
                setSuccess(`Sincronização concluída: ${updated} evento(s) actualizados, ${skipped} sem entidade (mantêm STARTER).`);
              } catch (e: any) {
                setError(e.response?.data?.error || e.message || 'Erro ao sincronizar');
              } finally {
                setSyncing(false);
              }
            }}
          >
            {syncing ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Sincronizar Todos os Eventos
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo Plano
          </Button>
        </Box>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Plans table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Plano</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Preço</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Eventos/mês</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Bilhetes máx.</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Equipa</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Comissão</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Funcionalidades</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Estado</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map(plan => {
                const activeFeat = FEATURES.filter(f => plan[f.key]);
                return (
                  <TableRow key={plan.id} hover sx={{ opacity: plan.ativo ? 1 : 0.5 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {plan.destaque && <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
                        <Typography variant="body2" fontWeight={600}>{plan.nome}</Typography>
                      </Box>
                      {plan.descricao && (
                        <Typography variant="caption" color="text.secondary">{plan.descricao}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={plan.codigo} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{plan.preco_label || '—'}</Typography>
                      {plan.preco_mensal != null && (
                        <Typography variant="caption" color="text.secondary">{plan.preco_mensal} CVE/mês</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{plan.eventos_mes ?? <em style={{ opacity: 0.5 }}>∞</em>}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{plan.bilhetes_max != null ? plan.bilhetes_max.toLocaleString() : <em style={{ opacity: 0.5 }}>∞</em>}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{plan.membros_equipa ?? <em style={{ opacity: 0.5 }}>∞</em>}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{plan.comissao_pct != null ? `${plan.comissao_pct}%` : '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {activeFeat.length === 0
                          ? <Typography variant="caption" color="text.disabled">Nenhuma</Typography>
                          : activeFeat.map(f => (
                              <Chip key={String(f.key)} label={f.label} size="small"
                                sx={{ fontSize: 10, height: 20, bgcolor: 'primary.50', color: 'primary.main' }} />
                            ))
                        }
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={plan.ativo ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={plan.ativo ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(plan)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => { setDeletingPlan(plan); setDeleteDialogOpen(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.disabled' }}>
                    Sem planos configurados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>
            {editingPlan ? `Editar — ${editingPlan.nome}` : 'Novo Plano'}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Grid container spacing={2}>
            {/* Identidade */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Identidade</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Código *" value={form.codigo}
                onChange={e => setF('codigo', e.target.value.toUpperCase())}
                placeholder="ex: STARTER" helperText="Identificador único (maiúsculas)" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Nome *" value={form.nome}
                onChange={e => setF('nome', e.target.value)} placeholder="ex: Pro" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Ordem" type="number" value={form.ordem}
                onChange={e => setF('ordem', Number(e.target.value))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Descrição" value={form.descricao || ''}
                onChange={e => setF('descricao', e.target.value)} placeholder="Breve descrição pública" />
            </Grid>

            <Grid size={12}><Divider /></Grid>

            {/* Preço */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Preço</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Label de preço" value={form.preco_label || ''}
                onChange={e => setF('preco_label', e.target.value)}
                placeholder="ex: Gratuito / Sob consulta" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Preço mensal (CVE)" type="number"
                value={form.preco_mensal ?? ''} placeholder="vazio = sem preço fixo"
                onChange={e => setF('preco_mensal', numOrNull(e.target.value))}
                InputProps={{ endAdornment: <InputAdornment position="end">CVE</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Comissão por bilhete (%)" type="number"
                value={form.comissao_pct ?? ''} placeholder="vazio = negociável"
                onChange={e => setF('comissao_pct', numOrNull(e.target.value))}
                InputProps={{ endAdornment: <InputAdornment position="end"><PercentIcon fontSize="small" /></InputAdornment> }} />
            </Grid>

            <Grid size={12}><Divider /></Grid>

            {/* Limites */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Limites <span style={{ fontWeight: 400 }}>(vazio = ilimitado)</span></Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Eventos por mês" type="number"
                value={form.eventos_mes ?? ''} placeholder="∞"
                onChange={e => setF('eventos_mes', numOrNull(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start"><EventIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Bilhetes máximos" type="number"
                value={form.bilhetes_max ?? ''} placeholder="∞"
                onChange={e => setF('bilhetes_max', numOrNull(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start"><TicketIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Convidados máximos" type="number"
                value={form.convidados_max ?? ''} placeholder="∞"
                onChange={e => setF('convidados_max', numOrNull(e.target.value))}
                helperText="Limite de convidados por convite (email)"
                InputProps={{ startAdornment: <InputAdornment position="start"><PeopleIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Membros da equipa" type="number"
                value={form.membros_equipa ?? ''} placeholder="∞"
                onChange={e => setF('membros_equipa', numOrNull(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start"><PeopleIcon fontSize="small" /></InputAdornment> }} />
            </Grid>

            <Grid size={12}><Divider /></Grid>

            {/* Funcionalidades */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Funcionalidades</Typography>
            </Grid>
            {FEATURE_GROUPS.map(group => (
              <Grid size={12} key={group}>
                <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 0.5, display: 'block' }}>{group}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {FEATURES.filter(f => f.group === group).map(f => (
                    <Card key={String(f.key)} variant="outlined"
                      sx={{ minWidth: 200, flex: '1 1 200px', cursor: 'pointer', transition: 'all .15s',
                        borderColor: form[f.key] ? 'primary.main' : 'divider',
                        bgcolor: form[f.key] ? 'primary.50' : 'background.paper' }}
                      onClick={() => setF(f.key, !form[f.key])}>
                      <CardContent sx={{ py: '8px !important', px: 1.5 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={!!form[f.key]} onChange={e => setF(f.key, e.target.checked)} />}
                          label={<Typography variant="body2" fontWeight={form[f.key] ? 600 : 400}>{f.label}</Typography>}
                          sx={{ m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>
            ))}

            <Grid size={12}><Divider /></Grid>

            {/* Meta */}
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Meta</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={<Switch checked={form.destaque} onChange={e => setF('destaque', e.target.checked)} />}
                label={<><StarIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5, verticalAlign: 'middle' }} />Plano em destaque</>}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={<Switch checked={form.ativo} onChange={e => setF('ativo', e.target.checked)} />}
                label="Plano activo"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar plano</DialogTitle>
        <DialogContent>
          <Typography>Tem a certeza que quer eliminar o plano <strong>{deletingPlan?.nome}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acção não pode ser revertida. Se o plano estiver em uso por eventos existentes, a operação será bloqueada.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {deleting ? 'A eliminar…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
